import {
  Container,
  FullDialog,
  Typography,
  Box,
  Button,
  TextFieldController,
  RadioButton,
  RadioGroup,
  Grid,
  ActionLayout,
} from '@alice-financial/pretext-ui'
import * as React from 'react'
import { EmployeeRow } from './types'
import { useEmployeeUpcomingPaycheckQuery } from '@alice-financial/alice-sdk/src/graphql/employee/queries/employeeUpcomingPaycheck_gen'
import {
  useUpdateEmployeePayrollStatus,
  UpdateEmployeePayrollStatusMutation,
  useResponseNotification,
  formatCentsAsDollars,
} from '@alice-financial/alice-sdk'
import { EmployeePayrollSystemStatusEnum } from '@alice-financial/alice-sdk/src/graphql/generated.types'
import { useForm, Controller } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

type FinalPaycheckDialogProps = {
  open: boolean
  onClose: () => void
  employee: EmployeeRow
}

type TerminateFormValues = {
  terminationDate: string
  finalPaycheckDate: string
  isOffCycle: boolean
}

export const FinalPaycheckDialog = ({ open, onClose, employee }: FinalPaycheckDialogProps) => {
  const {
    data: upcomingPaycheckData,
    isLoading,
    error,
  } = useEmployeeUpcomingPaycheckQuery({ employeeId: employee.id }, { enabled: open && Boolean(employee) })

  const { notifySuccess, notifyError } = useResponseNotification()
  const { control, handleSubmit } = useForm<TerminateFormValues>({
    defaultValues: {
      terminationDate: '',
      finalPaycheckDate: '',
      isOffCycle: false,
    },
  })

  const { mutate: updatePayrollStatus, isLoading: isSubmitting } = useUpdateEmployeePayrollStatus({
    onSuccess: (data: UpdateEmployeePayrollStatusMutation) => {
      const response = data.updateEmployee
      if (!response?.employee) {
        notifyError('Error: No response from server')
        return
      }
      notifySuccess('Final paycheck will be processed by Alice')
      onClose()
    },
    onError: (error: Error) => {
      notifyError(`Error: ${error.message}`)
    },
  })

  const onSubmit = handleSubmit((values) => {
    updatePayrollStatus({
      employeeId: employee!.id,
      payrollStatus: {
        ...values,
        payrollSystemStatus: EmployeePayrollSystemStatusEnum.Terminated,
      },
    })
  })

  if (!open || !employee) return null

  return (
    <FullDialog
      open={open}
      onClose={onClose}
      title={
        <Typography variant="h2">
          Final paycheck and termination for: {employee.firstName} {employee.lastName}
        </Typography>
      }
    >
      <Container>
        <Typography variant="body2" gutterBottom>
          When {employee.firstName} {employee.lastName} has been or will be terminated, you{' '}
          <strong>must</strong> make sure final deductions and reimbursements run on their final paycheck.
          Completing this step is essential to remain in compliance and to avoid over-reimbursing plan funds.
        </Typography>
        <Typography variant="body2" gutterBottom>
          To set their final paycheck and terminate their benefits on the correct date, please complete the
          four steps on this panel and click CONFIRM.
        </Typography>
        <form onSubmit={onSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h3">Step 1</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2">Tell us how their final paycheck will be run.</Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="isOffCycle"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value.toString()}
                    onChange={(_, value) => field.onChange(value === 'true')}
                  >
                    <RadioButton value="false" label="On-cycle paycheck on the next scheduled pay date" />
                    <RadioButton
                      value="true"
                      label="Off-cycle paycheck on their termination date or another date"
                    />
                  </RadioGroup>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h3">Step 2</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2">
                Tell us about the termination date and the final paycheck date.
              </Typography>
            </Grid>

            <TextFieldController
              control={control}
              name="terminationDate"
              label="Employee Termination Date"
              type="date"
              required
              fullWidth
            />
            <TextFieldController
              control={control}
              name="finalPaycheckDate"
              label="Final Paycheck Date"
              type="date"
              required
              fullWidth
            />
            <Grid size={{ xs: 12 }}>
              <Typography variant="h3">Step 3</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2">
                If you selected off-cycle for final paycheck type, enter these amounts into their final
                paycheck before clicking CONFIRM. If you selected on-cycle, Alice will include this
                information in the next scheduled payroll run.
              </Typography>
            </Grid>

            {isLoading && <Typography>Loading paycheck info…</Typography>}
            {error && <Typography color="error">Failed to load paycheck info.</Typography>}
            {upcomingPaycheckData?.employee?.upcomingPaycheck && (
              <Box>
                <Typography variant="body2">
                  Mass Transit Deduction:{' '}
                  {formatCentsAsDollars(
                    upcomingPaycheckData.employee.upcomingPaycheck.massTransitDeduction,
                    false
                  )}
                </Typography>
                <Typography variant="body2">
                  Mass Transit Reimbursement:{' '}
                  {formatCentsAsDollars(
                    upcomingPaycheckData.employee.upcomingPaycheck.massTransitReimbursement,
                    false
                  )}
                </Typography>
                <Typography variant="body2">
                  Parking Deduction:{' '}
                  {formatCentsAsDollars(
                    upcomingPaycheckData.employee.upcomingPaycheck.parkingDeduction,
                    false
                  )}
                </Typography>
                <Typography variant="body2">
                  Parking Reimbursement:{' '}
                  {formatCentsAsDollars(
                    upcomingPaycheckData.employee.upcomingPaycheck.parkingReimbursement,
                    false
                  )}
                </Typography>
              </Box>
            )}

            <Grid size={{ xs: 12 }}>
              <Typography variant="h3">Step 4</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2">
                In this step you&apos;re confirming that this individual will be or has been terminated, that
                these dates are accurate, that you have or will run a final paycheck to ensure accurate
                benefits processing for your benefit plan, and that this individual will be opted out of their
                employee benefits.
              </Typography>
            </Grid>
          </Grid>
          <ActionLayout
            primary={
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Button variant="outlined" onClick={onClose}>
                    <FormattedMessage id="common.cancel.cancel" />
                  </Button>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Button variant="contained" type="submit" disabled={isSubmitting}>
                    Confirm
                  </Button>
                </Grid>
              </Grid>
            }
          />
        </form>
      </Container>
    </FullDialog>
  )
}
